package com.evoapps.evoshift.saves

import com.evoapps.evoshift.observability.AppTelemetry
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.saves_failed
import evoshift.composeapp.generated.resources.saves_recalculation_failed
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.CoroutineStart
import kotlinx.coroutines.Deferred
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.async
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.update

/** What a tracked job does: the app-wide indicator names it ("Saving…" or "Recalculating costs…"). */
enum class SaveKind { SAVE, RECALCULATE }

/**
 * A save the app is still busy with (or one that failed while nobody was waiting for it).
 * [key] names what is being saved ("employee:<workplace>:<uid>"); [payload] is what the screen
 * submitted, so a screen opened again during the save shows those values, not the stored ones.
 */
data class PendingSave(
    val id: Long,
    val key: String,
    val kind: SaveKind,
    val payload: Any? = null,
    /** Why it failed; null while it is running. A failed save waits for a retry or a dismissal. */
    val failure: UiText? = null
) {
    val isRunning: Boolean get() = failure == null
}

/** What the shell tells the person about a save no screen was waiting for any more. */
sealed interface SaveNotice {
    data class Saved(val message: UiText) : SaveNotice

    /** [id]: the failed save, for [PendingSaves.retry] / [PendingSaves.dismiss]. */
    data class Failed(val id: Long, val kind: SaveKind, val message: UiText) : SaveNotice

    /** SH-186: a save unanswered while offline: the change is in this device's cache and syncs once connected. */
    data object KeptOnDevice : SaveNotice
}

/** The same thing is still being saved: a second submit is refused, not run twice. */
class SaveInProgressException(val key: String) : IllegalStateException("Still saving $key")

/** A background recalculation failed (a non-fatal: the save itself is done). */
class BackgroundRecalculationException(val key: String, cause: Throwable) :
    Exception("Recalculation $key failed: ${cause::class.simpleName}: ${cause.message}", cause)

/**
 * SH-148: the app's saves, run to completion whatever happens to the screen that started them.
 *
 * A screen's view model is cleared when the person leaves it, and with it its viewModelScope:
 * a save launched there stopped halfway (a pay change written, the costs not repriced) and a
 * screen opened again knew nothing about it. Saves run here instead, in the app's own scope
 * (a singleton with its own [SupervisorJob], never cancelled), and are tracked by key:
 *
 * * [run]: the screen waits for the result as before, but leaving it only stops the waiting.
 *   A save nobody waits for any more reports itself through [notices] (the shell's snackbar,
 *   a failure with "Retry").
 * * [launch]: a background job nobody waits for (repricing the shifts after a pay change).
 * * [saves] / [observe]: what is pending, for the app-wide indicator and for a screen opened
 *   again during its save ([await] then follows it to the end).
 *
 * A second save of a key still running is refused ([SaveInProgressException]) unless it is
 * queued ([run]/[launch] with `queue = true`: it starts when the earlier one is done).
 */
class PendingSaves(
    private val scope: CoroutineScope = CoroutineScope(SupervisorJob() + Dispatchers.Main.immediate),
    private val reportNonFatal: (Throwable) -> Unit = { AppTelemetry.recordNonFatal(it) }
) {
    private class Tracked(
        val id: Long,
        val key: String,
        val kind: SaveKind,
        val successMessage: UiText?,
        val block: suspend () -> Result<Any?>
    ) {
        lateinit var deferred: Deferred<Result<Any?>>
        /** How many callers are waiting for this save ([run], [await]); none: it reports itself. */
        var waiting = 0
    }

    private var nextId = 1L
    private val tracked = mutableMapOf<Long, Tracked>()

    private val _saves = MutableStateFlow<List<PendingSave>>(emptyList())
    /** The pending and failed saves, oldest first. */
    val saves: StateFlow<List<PendingSave>> = _saves.asStateFlow()

    private val _notices = MutableSharedFlow<SaveNotice>(extraBufferCapacity = 16)
    /** Saves that ended while no screen waited for them (success messages, failures to retry). */
    val notices: SharedFlow<SaveNotice> = _notices.asSharedFlow()

    /** Whether a save of [key] is running. */
    fun isPending(key: String): Boolean = _saves.value.any { it.key == key && it.isRunning }

    /** The newest save of [key] (running or failed), or null. */
    fun entry(key: String): PendingSave? = _saves.value.lastOrNull { it.key == key }

    /** [entry] as it changes. */
    fun observe(key: String): Flow<PendingSave?> = saves.map { list -> list.lastOrNull { it.key == key } }.distinctUntilChanged()

    /**
     * Runs [block] in the app's scope and waits for it. Cancelling the caller (the screen left)
     * stops only the waiting: the save goes on and, if nobody waits for it at the end, reports
     * itself ([notices], [successMessage] on success). A save of [key] still running: refused
     * with [SaveInProgressException], or with [queue] run after it.
     */
    suspend fun <T> run(
        key: String,
        kind: SaveKind = SaveKind.SAVE,
        payload: Any? = null,
        successMessage: UiText? = null,
        queue: Boolean = false,
        block: suspend () -> Result<T>
    ): Result<T> {
        val job = start(key, kind, payload, successMessage, queue, block) ?: return Result.failure(SaveInProgressException(key))
        @Suppress("UNCHECKED_CAST")
        return follow(job) as Result<T>
    }

    /** Starts [block] with nobody waiting for it; false when refused (a save of [key] still running). */
    fun <T> launch(
        key: String,
        kind: SaveKind = SaveKind.SAVE,
        payload: Any? = null,
        successMessage: UiText? = null,
        queue: Boolean = false,
        block: suspend () -> Result<T>
    ): Boolean {
        val job = start(key, kind, payload, successMessage, queue, block) ?: return false
        job.deferred.start()
        return true
    }

    /** Waits for the running save of [key] (a screen opened again during it); null when none is running. */
    @Suppress("UNCHECKED_CAST")
    suspend fun <T> await(key: String): Result<T>? {
        val job = tracked.values.lastOrNull { it.key == key && !it.deferred.isCompleted } ?: return null
        return follow(job) as Result<T>
    }

    /** Runs a failed save again (the snackbar's "Retry"), with nobody waiting for it. */
    fun retry(id: Long) {
        val failed = tracked[id]?.takeIf { _saves.value.any { s -> s.id == id && !s.isRunning } } ?: return
        val again = Tracked(failed.id, failed.key, failed.kind, failed.successMessage, failed.block)
        tracked[id] = again
        _saves.update { list -> list.map { if (it.id == id) it.copy(failure = null) else it } }
        again.deferred = scope.async(start = CoroutineStart.LAZY) { execute(again, previous = null) }
        again.deferred.start()
    }

    /** SH-186: a screen stopped waiting for a save because the device is offline; the shell says so. */
    fun noticeKeptOnDevice() {
        _notices.tryEmit(SaveNotice.KeptOnDevice)
    }

    /** Forgets a failed save (the snackbar dismissed, or a screen took its values over). */
    fun dismiss(id: Long) {
        if (_saves.value.none { it.id == id && !it.isRunning }) return
        tracked.remove(id)
        _saves.update { list -> list.filterNot { it.id == id } }
    }

    @Suppress("UNCHECKED_CAST")
    private fun <T> start(
        key: String,
        kind: SaveKind,
        payload: Any?,
        successMessage: UiText?,
        queue: Boolean,
        block: suspend () -> Result<T>
    ): Tracked? {
        val previous = tracked.values.lastOrNull { it.key == key && !it.deferred.isCompleted }
        if (previous != null && !queue) return null
        // A newer save of the key replaces a failed one (the screen submitted again).
        _saves.value.filter { it.key == key && !it.isRunning }.forEach { tracked.remove(it.id); _saves.update { l -> l - it } }
        val job = Tracked(nextId++, key, kind, successMessage, block as suspend () -> Result<Any?>)
        tracked[job.id] = job
        _saves.update { it + PendingSave(job.id, key, kind, payload) }
        // Started by the caller once it counts as waiting, so a save that ends at once is not
        // reported twice (by the screen and by the shell).
        job.deferred = scope.async(start = CoroutineStart.LAZY) { execute(job, previous?.deferred) }
        return job
    }

    private suspend fun follow(job: Tracked): Result<Any?> {
        job.waiting++
        try {
            job.deferred.start()
            return job.deferred.await()
        } finally {
            job.waiting--
        }
    }

    private suspend fun execute(job: Tracked, previous: Deferred<*>?): Result<Any?> {
        previous?.join()
        val result = try {
            job.block()
        } catch (e: CancellationException) {
            throw e
        } catch (e: Throwable) {
            Result.failure(e)
        }
        val unattended = job.waiting == 0
        result.fold(
            onSuccess = {
                forget(job.id)
                if (unattended) job.successMessage?.let { _notices.tryEmit(SaveNotice.Saved(it)) }
            },
            onFailure = { e ->
                // A recalculation nobody sees failing still has to reach the crash reports.
                if (job.kind == SaveKind.RECALCULATE) reportNonFatal(BackgroundRecalculationException(job.key, e))
                if (unattended) {
                    val message = UiText.Resource(
                        if (job.kind == SaveKind.RECALCULATE) Res.string.saves_recalculation_failed else Res.string.saves_failed,
                        e.toUiText()
                    )
                    _saves.update { list -> list.map { if (it.id == job.id) it.copy(failure = message) else it } }
                    _notices.tryEmit(SaveNotice.Failed(job.id, job.kind, message))
                } else {
                    // The screen waiting for it says why and keeps the values for another try.
                    forget(job.id)
                }
            }
        )
        return result
    }

    private fun forget(id: Long) {
        tracked.remove(id)
        _saves.update { list -> list.filterNot { it.id == id } }
    }
}
